import {
  ROOMS_FETCH_REQUEST,
  ROOMS_FETCH_SUCCESS,
  ROOMS_FETCH_FAIL,
  ROOM_CREATE_SUCCESS,
  ROOM_JOIN_SUCCESS,
  ROOM_LEAVE_SUCCESS,
  ROOM_END_SUCCESS,
  ROOM_UPDATE,
  ROOM_CLEAR,
  ROOM_SET_PARTICIPANT_VOLUME,
  ROOM_CHAT_FETCH_SUCCESS,
  ROOM_CHAT_SEND_SUCCESS,
  ROOM_CHAT_NEW_MESSAGES,
  ROOM_CHAT_MESSAGE_DELETED,
  ROOM_RAID_PENDING,
  ROOM_RAID_DISMISSED,
} from 'mastodon/actions/rooms';
import type {
  ApiRoomJSON,
  ApiRoomChatMessageJSON,
} from 'mastodon/api_types/rooms';

// Ticket 64 - Raid Stream. What a viewer's poll discovered while they were in a
// room that has just been raided away.
export interface PendingRaid {
  targetRoomId: string;
  targetTitle: string | null;
  fromTitle: string;
}

interface RoomsState {
  list: ApiRoomJSON[];
  listLoading: boolean;
  listError: string | null;
  activeRoom: ApiRoomJSON | null;
  participantVolumes: Record<string, number>;
  chatMessages: ApiRoomChatMessageJSON[];
  // Deliberately OUTSIDE activeRoom: by the time this is set the viewer has
  // already been dropped from the room (the host ended it), so there is no
  // active room left to hang it on. It is the one piece of state that has to
  // survive a room disappearing.
  pendingRaid: PendingRaid | null;
}

const initialState: RoomsState = {
  list: [],
  listLoading: false,
  listError: null,
  activeRoom: null,
  participantVolumes: {},
  chatMessages: [],
  pendingRaid: null,
};

interface RoomsAction {
  type: string;
  rooms?: ApiRoomJSON[];
  room?: ApiRoomJSON;
  roomId?: string;
  error?: unknown;
  accountId?: string;
  volume?: number;
  chatMessages?: ApiRoomChatMessageJSON[];
  chatMessage?: ApiRoomChatMessageJSON;
  messageId?: string;
  raid?: PendingRaid;
  // ROOM_CREATE_SUCCESS only: false = started without entering (ticket 111).
  enter?: boolean;
}

// Poll payloads (5s list poll, 3s active-room poll) usually carry the exact
// same data as the previous tick. Deep-compare via JSON so unchanged polls
// preserve object identity — otherwise every subscriber (sidebar, video
// stage, chat) re-renders on every tick. Payloads are capped (≤30 rooms), so
// stringify is cheap.
const sameJSON = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

export const roomsReducer = (
  state: RoomsState = initialState,
  action: RoomsAction,
): RoomsState => {
  switch (action.type) {
    case ROOMS_FETCH_REQUEST:
      // Background refreshes (list already populated) don't flip listLoading:
      // the flag only means "initial load in progress", and toggling it every
      // poll tick churns state identity for nothing.
      if (state.list.length > 0) {
        return state.listError === null ? state : { ...state, listError: null };
      }
      return { ...state, listLoading: true, listError: null };

    case ROOMS_FETCH_SUCCESS: {
      const rooms = action.rooms ?? [];
      if (sameJSON(rooms, state.list)) {
        return state.listLoading ? { ...state, listLoading: false } : state;
      }
      return { ...state, listLoading: false, list: rooms };
    }

    case ROOMS_FETCH_FAIL:
      return { ...state, listLoading: false, listError: String(action.error) };

    case ROOM_CREATE_SUCCESS:
      return {
        ...state,
        list: action.room ? [action.room, ...state.list] : state.list,
        // Not entering still clears any room we were in: the server has just
        // taken us out of it (one room at a time).
        activeRoom: action.enter === false ? null : (action.room ?? null),
      };

    case ROOM_JOIN_SUCCESS:
    case ROOM_UPDATE: {
      const room = action.room;
      if (!room) {
        return state.activeRoom === null
          ? state
          : { ...state, activeRoom: null };
      }
      // Ticket 64: landing in a room settles any raid that was carrying you -
      // including a raid into a DIFFERENT room than the one you ended up in,
      // because by then you have made your own choice about where to be.
      const raid = action.type === ROOM_JOIN_SUCCESS ? null : state.pendingRaid;
      if (sameJSON(room, state.activeRoom) && raid === state.pendingRaid)
        return state;
      return {
        ...state,
        activeRoom: room,
        pendingRaid: raid,
        list: state.list.map((r) => (r.id === room.id ? room : r)),
      };
    }

    case ROOM_LEAVE_SUCCESS:
      return {
        ...state,
        activeRoom: null,
        participantVolumes: {},
        chatMessages: [],
      };

    case ROOM_END_SUCCESS: {
      const ended = action.room;
      return {
        ...state,
        activeRoom: null,
        participantVolumes: {},
        chatMessages: [],
        list: ended ? state.list.filter((r) => r.id !== ended.id) : state.list,
      };
    }

    // Ticket 64. Set immediately AFTER ROOM_LEAVE_SUCCESS, which is what makes
    // the ordering here matter: leaving clears the room, and this is the note
    // that says where the person who was in it should go next.
    case ROOM_RAID_PENDING:
      if (!action.raid) return state;
      // Already carrying this raid: keep the existing object so the countdown
      // component is not remounted by the next 3s poll re-announcing it.
      if (state.pendingRaid?.targetRoomId === action.raid.targetRoomId)
        return state;
      return { ...state, pendingRaid: action.raid };

    // The viewer said no, or has been carried across. Either way the notice is
    // spent, and it must not come back on the next poll of the old room.
    case ROOM_RAID_DISMISSED:
      return state.pendingRaid === null
        ? state
        : { ...state, pendingRaid: null };

    case ROOM_CLEAR:
      return {
        ...state,
        activeRoom: null,
        participantVolumes: {},
        chatMessages: [],
        pendingRaid: null,
      };

    case ROOM_CHAT_FETCH_SUCCESS:
      return {
        ...state,
        chatMessages: (action.chatMessages ?? []).filter((m) => !m.deleted),
      };

    case ROOM_CHAT_SEND_SUCCESS:
      return {
        ...state,
        chatMessages: action.chatMessage
          ? [...state.chatMessages, action.chatMessage]
          : state.chatMessages,
      };

    case ROOM_CHAT_NEW_MESSAGES: {
      if (!action.chatMessages || action.chatMessages.length === 0)
        return state;

      // Tombstones (deleted: true) remove the message wherever it sits in the
      // local list; live messages append if unseen.
      const deletedIds = new Set(
        action.chatMessages.filter((m) => m.deleted).map((m) => m.id),
      );
      const kept =
        deletedIds.size > 0
          ? state.chatMessages.filter((m) => !deletedIds.has(m.id))
          : state.chatMessages;

      const existingIds = new Set(kept.map((m) => m.id));
      const newMsgs = action.chatMessages.filter(
        (m) => !m.deleted && !existingIds.has(m.id),
      );

      return newMsgs.length > 0 || kept !== state.chatMessages
        ? { ...state, chatMessages: [...kept, ...newMsgs] }
        : state;
    }

    case ROOM_CHAT_MESSAGE_DELETED:
      return action.messageId
        ? {
            ...state,
            chatMessages: state.chatMessages.filter(
              (m) => m.id !== action.messageId,
            ),
          }
        : state;

    case ROOM_SET_PARTICIPANT_VOLUME:
      return {
        ...state,
        participantVolumes:
          action.accountId != null && action.volume != null
            ? { ...state.participantVolumes, [action.accountId]: action.volume }
            : state.participantVolumes,
      };

    default:
      return state;
  }
};
